import isNil from 'lodash/isNil';
import omitBy from 'lodash/omitBy';

import {getEventValueAndLabel} from 'common/report/util/events';
import {findKeyByValue} from 'common/util/typescript';
import {
  ALL_MATH_TYPES,
  DATASET_MIXPANEL,
  FUNNELS_MATH_TYPES,
  PER_USER_AGGREGATIONS,
  PRIMITIVE_MATH_TYPES,
  ResourceType,
  RETENTION_MATH_TYPES,
} from 'common/report/constants';
import {TOP_PEOPLE} from 'common/query-builder/query-block/properties-menu/constants';
import {
  CohortItem,
  FiltersOperator,
  isEventEntry,
  isFormulaEntry,
  isInlineCohortItem,
  isMetricEntry,
  isSavedCohortItem,
  MetricEntry,
  MetricEntryBehavior,
  MetricEntryMeasurement,
  QueryFormulaEntry,
  QueryMetricEntry,
} from 'common/query-builder/query-entry-section/types';
import {MetricBehavior} from 'common/report/insights/util/types';
import {BehaviorShowClause, ShowClause} from 'common/report/insights/models/show-clause';
import {FilterClause} from 'common/report/insights/models/filter-clause';
import {ShowSection} from 'common/report/insights/models/section';
import {formatSource} from 'common/report/insights/util';
import {isProperty} from 'common/query-builder/query-block/properties-menu/types';
import {DataGroup} from 'common/widgets/data-group-selector/types';
import {PropertySectionItem} from 'common/query-builder/query-block/properties-menu/item-menu-types';
import {ConversionWindowUnit, FunnelOrder} from 'common/report/funnels/models/constants';
import {BirthIntervalUnit, RetentionAlignmentType, RetentionType} from 'common/report/retention/util/types';
import {TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {SectionItem} from 'common/components/items-menu/types';
import {getRetentionBucketName} from 'common/report/retention/util';
import {MetricType} from 'common/query-builder/query-block/constants';

import {QUERY_BUILDER_RESOURCE_TYPE_MAPPING} from '../models/constants';
import {isFormulaShowClause} from '../models/utils';
import {ReportSectionsMetadata, ShowClause as ShowClauseType} from '../util/types';
import {DETERMINER_MAPPING} from './constants';
import {BehaviorMetric, InsightsQueryError, InsightsResourceType} from './types';

/** operators in the legacy per-user format */
const LEGACY_PER_USER_OPERATORS = [
  ALL_MATH_TYPES.AVERAGE,
  ALL_MATH_TYPES.MEDIAN,
  ALL_MATH_TYPES.MAX,
  ALL_MATH_TYPES.MIN,
  ALL_MATH_TYPES.P90,
  ALL_MATH_TYPES.P75,
  ALL_MATH_TYPES.P25,
];

function getDataGroupLabel(dataGroupId, dataGroups = []) {
  const group = dataGroups.find((item) => item.groupID === dataGroupId);

  return group ? formatSource(group.displayName, {all: true, pluralizeDataGroup: true}) : TOP_PEOPLE.label;
}

/**
 * Transforms show clauses to event entries
 */
export function getEntries({
  showClauses,
  dataGroups = [],
  queryError,
  dateUnit,
  reportSectionsMetadata,
}: {
  showClauses: ShowClauseType[];
  dataGroups?: DataGroup[];
  queryError?: InsightsQueryError;
  dateUnit?: TimeUnit;
  reportSectionsMetadata?: ReportSectionsMetadata;
}): Array<QueryMetricEntry | MetricEntry> {
  // TODO opslead
  const entries = [];
  let formulaIdx = 0;
  const formulaErrorByIdx = new Map<number, string>(
    (queryError?.formula_errors || []).map(({index, message}) => [index, message]),
  );
  // eslint-disable-next-line sonarjs/cognitive-complexity
  showClauses.forEach((clause: ShowClauseType, index: number) => {
    if (isFormulaShowClause(clause)) {
      const {rolling, cumulative, multiAttribution} = clause.measurement;
      const error = formulaErrorByIdx.get(formulaIdx);
      const entry: QueryFormulaEntry = {
        isHidden: clause.isHidden ?? false,
        isExpanded: clause.isExpanded ?? false,
        type: ResourceType.Formula,
        event: omitBy({...clause.formula, error}, isNil),
        filters: [],
        rolling,
        cumulative,
        multiAttribution,
      };
      entries.push(entry);
      formulaIdx += 1;
      return;
    }

    const {behavior, measurement} = clause;
    if (behavior.type === MetricType.Funnel) {
      const metricBehavior: MetricEntryBehavior = {
        // Common behavior fields
        type: behavior.type,
        dataGroupId: behavior.dataGroupId,
        name: getMetricName(behavior),
        renamed: behavior.renamed,
        behaviors: getMetricEntryBehaviors(behavior), // for inline-custom-event, inline-funnel, inline-retention
        // Funnels specific fields
        funnelOrder: behavior.funnelOrder || FunnelOrder.Loose,
        exclusions: behavior.exclusions || [],
        aggregateBy: behavior.aggregateBy || [],
        conversionWindowDuration: behavior.conversionWindowDuration || 7,
        conversionWindowUnit: behavior.conversionWindowUnit || ConversionWindowUnit.Day,
      };
      const metricMeasurement: MetricEntryMeasurement = {
        property: convertBookmarkPropertyToPropertySelection(measurement.property, behavior),
        aggregationOperator: measurement.math,
        cumulative: Boolean(measurement.cumulative),
        // funnel step
        stepIndex: measurement.stepIndex,
        countUsersOnceType: measurement.segmentMethod ? measurement.segmentMethod : null,
        attributionSelection: measurement.actionMode === `step` ? measurement.actionStep : measurement.actionMode,
        multiAttribution: measurement.multiAttribution,
      };
      const metricEntry: MetricEntry = {
        type: `metric-entry`,
        isHidden: clause.isHidden ?? false,
        isExpanded: clause.isExpanded ?? false,
        behavior: metricBehavior,
        measurement: metricMeasurement,
      };

      entries.push(metricEntry);
      return;
    }
    if (behavior.type === MetricType.Retention || behavior.type === MetricType.RetentionFrequency) {
      const metricBehavior: MetricEntryBehavior = {
        // Common behavior fields
        type: behavior.type,
        dataGroupId: behavior.dataGroupId,
        name: getMetricName(behavior),
        renamed: behavior.renamed,
        behaviors: getMetricEntryBehaviors(behavior), // for inline-custom-event, inline-funnel, inline-retention
        // Retention specific fields
        retentionType: behavior.type === MetricType.Retention ? `birth` : `addiction`,
        retentionAlignmentType: behavior.retentionAlignmentType || RetentionAlignmentType.Birth,
        retentionUnit: behavior.retentionUnit || TimeUnit.Day,
        retentionUnbounded: behavior.retentionUnbounded,
        retentionCustomBucketSizes: behavior.retentionCustomBucketSizes || [],
      };
      const metricMeasurement: MetricEntryMeasurement = {
        // TODO: convert property type properly
        property: (measurement.property as unknown) as PropertySectionItem,
        aggregationOperator: measurement.math,
        retentionCumulative: Boolean(measurement.retentionCumulative),
        retentionBucketIndex: measurement.retentionBucketIndex,
        retentionBuckets: generateRetentionBuckets({
          showIndex: index,
          retentionBucketIndex: measurement.retentionBucketIndex,
          retentionType: behavior.type === MetricType.Retention ? `birth` : `addiction`,
          retentionUnit: behavior.retentionUnit,
          dateUnit,
          retentionCustomBucketSizes: behavior.retentionCustomBucketSizes,
          reportSectionsMetadata,
        }),
        countUsersOnceType: measurement.segmentMethod ? measurement.segmentMethod : null,
        attributionSelection: measurement.retentionSegmentationEvent,
        multiAttribution: measurement.multiAttribution,
      };
      const metricEntry: MetricEntry = {
        type: `metric-entry`,
        isHidden: clause.isHidden ?? false,
        isExpanded: clause.isExpanded ?? false,
        behavior: metricBehavior,
        measurement: metricMeasurement,
      };

      entries.push(metricEntry);
      return;
    }

    const type = findKeyByValue(QUERY_BUILDER_RESOURCE_TYPE_MAPPING, behavior.resourceType) || ResourceType.Event;

    const props = {
      type: null,
      isHidden: clause.isHidden ?? false,
      isExpanded: clause.isExpanded ?? false,
      aggregationOperator: measurement.math,
      aggregationOperatorPerUser: measurement.perUserAggregation,
      dataGroupId: behavior.dataGroupId,
      property: null,
      event: {
        custom: behavior.type === MetricType.CustomEvent,
        id: behavior.id,
        renamed: behavior.renamed,
      } as QueryMetricEntry['event'],
      filters: behavior.filters
        ? behavior.filters.map((filterClause) => new FilterClause(filterClause).toPropertyFilter())
        : [],
      filtersOperator: behavior.filters?.length
        ? findKeyByValue(DETERMINER_MAPPING, behavior.filtersDeterminer)
        : FiltersOperator.And,
      readOnlyEvent: type === ResourceType.User,
      ...(measurement.math === ALL_MATH_TYPES.HISTOGRAM ? {customBucket: behavior.customBucket} : {}),
      ...(measurement.multiAttribution ? {multiAttribution: measurement.multiAttribution} : {}),
      ...(measurement.rolling ? {rolling: measurement.rolling} : {}),
      ...(measurement.cumulative ? {cumulative: measurement.cumulative} : {}),
      ...(measurement.segmentMethod ? {countUsersOnceType: measurement.segmentMethod} : {}),
    };

    const {type: behaviorType, id, raw_cohort: rawCohort, name, dataGroupId, renamed} = behavior;
    switch (type) {
      case ResourceType.Cohort: {
        const inlineCohort = rawCohort;
        const cohortItem: CohortItem = inlineCohort
          ? {
              resourceType: ResourceType.Cohort,
              label: name,
              value: {
                cohort: {
                  raw_cohort: rawCohort,
                  name,
                },
              },
            }
          : {
              resourceType: ResourceType.Cohort,
              label: name,
              value: id,
              ...(renamed ? {renamed} : {}),
              ...(dataGroupId ? {data_group_id: dataGroupId} : {}),
            };
        props.type = ResourceType.Cohort;
        props.event = cohortItem;
        break;
      }
      case ResourceType.User:
      case ResourceType.Event: {
        let {value: eventValue, label} = getEventValueAndLabel({
          name: behaviorType === MetricType.CustomEvent ? null : name,
          custom: behaviorType === MetricType.CustomEvent,
          id,
        });
        // get user type dataGroupId label
        if (type === ResourceType.User) {
          if (dataGroupId) {
            label = getDataGroupLabel(dataGroupId, dataGroups || []);
          } else {
            label = TOP_PEOPLE.label;
          }
        }

        props.type = type;
        props.event = {
          custom: behaviorType === MetricType.CustomEvent,
          label: eventValue ? label : `Select Event`,
          value: eventValue,
          ...(renamed ? {renamed} : {}),
          ...(id ? {id} : {}),
        };
        break;
      }
    }

    props.property = convertBookmarkPropertyToPropertySelection(measurement.property, behavior);

    // migrate old aggregation format
    if (
      !measurement.property &&
      // this is not in the type def, so doing some hack
      LEGACY_PER_USER_OPERATORS.includes(props.aggregationOperator as typeof LEGACY_PER_USER_OPERATORS[number])
    ) {
      props.aggregationOperatorPerUser = PER_USER_AGGREGATIONS.TOTAL;
    }

    entries.push(props);
  });

  return entries;
}

function convertBookmarkPropertyToPropertySelection(bookmarkProperty, behavior): PropertySectionItem {
  if (bookmarkProperty) {
    const {
      listItemGroup = null,
      customProperty = null,
      customPropertyId = null,
      dataGroupId = null,
      tempDataGroupId = null,
      defaultType = null,
      joinPropertyType = null,
      propertyObjectKey = null,
      scdEvent = null,
      name,
      type,
      unit = null,
      label = null,
      resourceType: insightsResourceType,
    } = bookmarkProperty;
    const resourceType = findKeyByValue(QUERY_BUILDER_RESOURCE_TYPE_MAPPING, insightsResourceType);
    return {
      resourceType,
      dataGroupId: dataGroupId ?? behavior.dataGroupId,
      tempDataGroupId: tempDataGroupId ?? null,
      propertyDefaultType: defaultType,
      type,
      unit,
      value: name,
      label: label ? label : name,
      ...(propertyObjectKey ? {propertyObjectKey} : {}),
      ...(customProperty ? {customProperty} : {}),
      ...(customPropertyId ? {customPropertyId} : {}),
      ...(joinPropertyType ? {joinPropertyType} : {}),
      ...(listItemGroup ? {listItemGroup} : {}),
      ...(scdEvent ? {scdEvent} : {}),
    };
  } else {
    return null;
  }
}

// eslint-disable-next-line sonarjs/cognitive-complexity
function getMetricName(behavior: BehaviorMetric['behavior']): string {
  const {type} = behavior;
  if (type === MetricType.People) {
    return `All User Profiles`;
  }
  if (behavior.type === MetricType.Funnel) {
    const behaviors = behavior.behaviors;
    const hasEventComparison = behaviors.some((beh) => !!beh.behaviors);
    if (hasEventComparison) {
      return `Step 1 through Step ${behaviors.length}`;
    }
    if (behaviors.length < 2) {
      return null;
    }
    const firstBehavior = behaviors[0];
    const lastBehavior = behaviors[behaviors.length - 1];
    if (firstBehavior.name && lastBehavior.name) {
      const firstStep = getEventValueAndLabel({
        name: firstBehavior.type === MetricType.CustomEvent ? null : firstBehavior.name,
        custom: firstBehavior.type === MetricType.CustomEvent,
        id: firstBehavior.id,
      });
      const lastStep = getEventValueAndLabel({
        name: lastBehavior.type === MetricType.CustomEvent ? null : lastBehavior.name,
        custom: lastBehavior.type === MetricType.CustomEvent,
        id: lastBehavior.id,
      });
      return `${firstStep.label} through ${lastStep.label}`;
    }
  }
  if (behavior.type === MetricType.Retention) {
    if (behavior.behaviors.length < 2) {
      return null;
    }
    const firstBehavior = behavior.behaviors[0];
    const lastBehavior = behavior.behaviors[behavior.behaviors.length - 1];
    if (firstBehavior.name && lastBehavior.name) {
      const bornEvent = getEventValueAndLabel({
        name: firstBehavior.type === MetricType.CustomEvent ? null : firstBehavior.name,
        custom: firstBehavior.type === MetricType.CustomEvent,
        id: firstBehavior.id,
      });
      const event = getEventValueAndLabel({
        name: lastBehavior.type === MetricType.CustomEvent ? null : lastBehavior.name,
        custom: lastBehavior.type === MetricType.CustomEvent,
        id: lastBehavior.id,
      });
      return `${bornEvent.label} and then ${event.label}`;
    }
  }
  if (behavior.type === MetricType.RetentionFrequency) {
    if (behavior.behaviors.length !== 1) {
      return null;
    }
    if (behavior.behaviors[0].name) {
      const event = getEventValueAndLabel({
        name: behavior.behaviors[0].type === MetricType.CustomEvent ? null : behavior.behaviors[0].name,
        custom: behavior.behaviors[0].type === MetricType.CustomEvent,
        id: behavior.behaviors[0].id,
      });
      return `Frequency of ${event.label}`;
    }
  }
  if (behavior.type === MetricType.CustomEvent) {
    return null;
  }
  return behavior.name;
}

function convertBehaviorToMetricEntryBehavior(behavior: MetricBehavior): MetricEntryBehavior {
  return {
    id: behavior.id,
    type: behavior.type,
    name: behavior.name,
    renamed: behavior.renamed,
    filters: behavior.filters?.map((filter) => new FilterClause(filter).toPropertyFilter()),
    // filtersOperator is the key qb uses
    filtersOperator: behavior.filtersDeterminer === `any` ? FiltersOperator.Or : FiltersOperator.And,
    // filtersDeterminer ** SHOULD ** be the key to use, for now it's not
    filtersDeterminer: behavior.filtersDeterminer,
    funnelOrder: behavior.funnelOrder,
  };
}

function getMetricEntryBehaviors(behavior: BehaviorMetric['behavior']): MetricEntryBehavior[] {
  if (behavior.type === MetricType.Funnel) {
    return (behavior.behaviors || []).map((behavior) => {
      if (behavior.behaviors) {
        const compareBehaviors = behavior.behaviors;
        return {
          type: MetricType.Funnel,
          id: null,
          name: null,
          funnelOrder: behavior.funnelOrder,
          behaviors: compareBehaviors.map((beh) => convertBehaviorToMetricEntryBehavior(beh)),
        };
      }
      return convertBehaviorToMetricEntryBehavior(behavior);
    });
  }
  if (behavior.type === MetricType.Retention || behavior.type === MetricType.RetentionFrequency) {
    return (behavior.behaviors || []).map((behavior) => ({
      ...(behavior?.name?.startsWith(`$custom_event:`) ? {id: parseInt(behavior.name.split(`:`)[1], 10)} : {}),
      type: behavior?.name?.startsWith(`$custom_event:`) ? MetricType.CustomEvent : MetricType.Event,
      name: behavior.name,
      filters: behavior.filters?.length
        ? behavior.filters.map((filterClause) => new FilterClause(filterClause).toPropertyFilter())
        : [],
      filtersDeterminer: behavior.filtersDeterminer || `all`,
    }));
  }

  return [];
}

/**
 * Transforms event entries to show sections
 */
// eslint-disable-next-line sonarjs/cognitive-complexity
export function getShowSection({
  entries,
  dataGroupId,
  attributionSelection,
  queryType,
}: {
  entries: Array<QueryMetricEntry | MetricEntry>;
  dataGroupId?: string;
  attributionSelection?: number | BehaviorShowClause['measurement']['actionMode'];
  queryType?: MetricType;
}): ShowSection {
  let showSection: ShowSection;

  if (entries.length) {
    const showClauses = entries.map((entry) => {
      if (isFormulaEntry(entry)) {
        const {id, name, definition, referencedMetrics} = entry.event;
        return ShowClause.createShowClause({
          isHidden: entry.isHidden ?? false,
          isExpanded: entry.isExpanded ?? false,
          formula: omitBy({id, name, definition, referencedMetrics}, isNil),
          measurement: {
            rolling: entry.rolling,
            cumulative: entry.cumulative,
            multiAttribution: entry.multiAttribution,
          },
        });
      }

      if (isMetricEntry(entry)) {
        if (entry.behavior.type === MetricType.Funnel) {
          return createFunnelMetricShowClause(entry, attributionSelection);
        }
        if (entry.behavior.type === MetricType.Retention) {
          const metricType = queryType ?? MetricType.Retention;
          return createRetentionMetricShowClause(metricType, entry, attributionSelection);
        }
        if (entry.behavior.type === MetricType.RetentionFrequency) {
          const metricType = queryType ?? MetricType.RetentionFrequency;
          return createRetentionMetricShowClause(metricType, entry, attributionSelection);
        }
        return null;
      }
      const {
        event,
        filters,
        property,
        filtersOperator,
        aggregationOperator,
        type,
        aggregationOperatorPerUser,
        rolling,
        cumulative,
        countUsersOnceType,
      } = entry;
      const filterClauses = filters.map((filter) => FilterClause.fromPropertyFilter(filter));

      let showFilterSection = {};
      if (filterClauses.length) {
        showFilterSection = {
          filters: filterClauses,
          filtersDeterminer: DETERMINER_MAPPING[filtersOperator],
        };
      }

      const attrs: BehaviorMetric = {
        isHidden: entry.isHidden ?? false,
        isExpanded: entry.isExpanded ?? false,
        behavior: {
          type: MetricType.Event,
          resourceType: null,
          dataGroupId: entry.dataGroupId,
          dataset: DATASET_MIXPANEL,
          filters: [],
          ...showFilterSection,
          ...(isEventEntry(entry) && aggregationOperator === ALL_MATH_TYPES.HISTOGRAM
            ? {customBucket: entry.customBucket}
            : {}),
        },
        measurement: {
          math: aggregationOperator || PRIMITIVE_MATH_TYPES.TOTAL,
          perUserAggregation: (aggregationOperatorPerUser as PER_USER_AGGREGATIONS) || null,
          rolling: rolling || null,
          cumulative: cumulative || false,
          segmentMethod: countUsersOnceType,
          property: null,
          ...(isEventEntry(entry) && entry.multiAttribution ? {multiAttribution: entry.multiAttribution} : {}),
        },
      };

      switch (type) {
        case ResourceType.Cohort: {
          attrs.behavior.type = MetricType.Cohort;
          attrs.behavior.resourceType = InsightsResourceType.Cohorts;
          if (isInlineCohortItem(event)) {
            const cohortValue = event.value.cohort;

            attrs.behavior.name = event.label;
            attrs.behavior.raw_cohort = cohortValue.raw_cohort;
          } else if (isSavedCohortItem(event)) {
            attrs.behavior.id = event.value;
            attrs.behavior.name = event.label;
            attrs.behavior.renamed = event.renamed;
          }
          break;
        }
        case ResourceType.User:
        case ResourceType.Event:
          if (type === ResourceType.Event && event.custom) {
            attrs.behavior.type = MetricType.CustomEvent;
          }
          if (type === ResourceType.Event && !event.custom) {
            attrs.behavior.type = MetricType.Event;
          }
          if (type === ResourceType.User) {
            attrs.behavior.type = MetricType.People;
          }
          attrs.behavior.resourceType = QUERY_BUILDER_RESOURCE_TYPE_MAPPING[type];
          attrs.behavior.name = event.value;
          attrs.behavior.id = event.id;
          attrs.behavior.renamed = event.renamed;
          break;
      }

      if (property) {
        attrs.measurement.property = convertPropertySelectiontoBookmarkProperty(property);
      }
      return ShowClause.createShowClause(attrs);
    });

    showSection = new ShowSection(showClauses);
  } else {
    showSection = ShowSection.createShowSection({
      behavior: {
        dataGroupId,
        type: MetricType.Event,
        // Cleanup: Missing resourceType
        resourceType: undefined,
      },
    });
  }

  return showSection;
}

function createFunnelMetricShowClause(
  entry: MetricEntry,
  attributionSelection: number | BehaviorShowClause['measurement']['actionMode'],
) {
  const behavior: BehaviorShowClause[`behavior`] = {
    type: MetricType.Funnel,
    name: entry.behavior.name,
    renamed: entry.behavior.renamed,
    resourceType: null,
    dataGroupId: entry.behavior.dataGroupId,
    dataset: DATASET_MIXPANEL,
    filters: [],
    behaviors: entry.behavior.behaviors.map((behavior) => {
      if (`behaviors` in behavior) {
        const newCompareBehaviors = behavior.behaviors.map((beh) => {
          return {
            id: beh.id as number,
            type: beh.type,
            name: beh.name,
            renamed: beh.renamed,
            filters: beh.filters?.map((filter) => FilterClause.fromPropertyFilter(filter)),
            filtersDeterminer: beh.filtersDeterminer,
          };
        });
        return {
          type: behavior.type,
          funnelOrder: behavior.funnelOrder,
          behaviors: newCompareBehaviors,
        } as MetricBehavior;
      } else {
        return {
          id: behavior.id as number,
          type: behavior.type,
          name: behavior.name,
          renamed: behavior.renamed,
          filters: behavior.filters?.map((filter) => FilterClause.fromPropertyFilter(filter)),
          filtersDeterminer: behavior.filtersDeterminer,
          funnelOrder: behavior.funnelOrder ? behavior.funnelOrder : FunnelOrder.Loose,
        };
      }
    }),
    conversionWindowDuration: entry.behavior.conversionWindowDuration,
    conversionWindowUnit: entry.behavior.conversionWindowUnit,
    funnelOrder: entry.behavior.funnelOrder,
    exclusions: entry.behavior.exclusions || [],
    aggregateBy: entry.behavior.aggregateBy,
  };
  // attributionSelection is now set on the entry measurement for report_consistency instead
  // of being set directly on the query builder update selection
  const entryAttributionSelection = attributionSelection || entry.measurement.attributionSelection;
  const measurement: BehaviorShowClause[`measurement`] = {
    // TODO: change to funnelStepIndex
    stepIndex: entry.measurement.stepIndex,
    math: entry.measurement.aggregationOperator || FUNNELS_MATH_TYPES.CONVERSION_RATE_UNIQUE,
    perUserAggregation: null,
    rolling: null,
    cumulative: false,
    ...(entryAttributionSelection
      ? {
          actionMode: typeof entryAttributionSelection === `number` ? `step` : entryAttributionSelection,
          actionStep: typeof entryAttributionSelection === `number` ? entryAttributionSelection : null,
        }
      : {}),
    segmentMethod: entry.measurement.countUsersOnceType,
    property: entry.measurement.property?.value
      ? convertPropertySelectiontoBookmarkProperty(entry.measurement.property)
      : (entry.measurement[`property`] as any), // TODO the typing here is messed up
    multiAttribution: entry.measurement.multiAttribution,
  };
  return ShowClause.createShowClause({
    isHidden: entry.isHidden ?? false,
    isExpanded: entry.isExpanded ?? false,
    behavior,
    measurement,
  });
}

function createRetentionMetricShowClause(
  metricType: MetricType,
  entry: MetricEntry,
  attributionSelection: number | BehaviorShowClause['measurement']['actionMode'],
) {
  const bornBehavior = entry.behavior.behaviors[0];
  const subBehaviors = [
    {
      id: bornBehavior?.id as number,
      type: bornBehavior?.type,
      name: bornBehavior?.name,
      filters: bornBehavior?.filters?.map((filter) => FilterClause.fromPropertyFilter(filter)),
      filtersDeterminer: bornBehavior?.filtersDeterminer ?? `all`,
    },
  ];
  if (metricType === MetricType.Retention) {
    const retainedBehavior = entry.behavior.behaviors[1];
    subBehaviors.push({
      id: retainedBehavior?.id as number,
      type: retainedBehavior?.type ?? MetricType.Event,
      name: retainedBehavior?.name,
      filters: retainedBehavior?.filters?.map((filter) => FilterClause.fromPropertyFilter(filter)) ?? [],
      filtersDeterminer: retainedBehavior?.filtersDeterminer ?? `all`,
    });
  }

  const behavior: BehaviorShowClause[`behavior`] = {
    type: metricType,
    resourceType: null,
    dataGroupId: entry.behavior.dataGroupId,
    dataset: DATASET_MIXPANEL,
    name: entry.behavior.name,
    renamed: entry.behavior.renamed,
    filters: [],
    behaviors: subBehaviors,
    retentionType: metricType === MetricType.Retention ? `birth` : `addiction`,
    retentionAlignmentType: entry.behavior.retentionAlignmentType,
    retentionUnit: entry.behavior.retentionUnit,
    retentionUnbounded: entry.behavior.retentionUnbounded,
    retentionCustomBucketSizes: entry.behavior.retentionCustomBucketSizes,
  };
  const entryAttributionSelection = attributionSelection || entry.measurement.attributionSelection;
  const retentionSegmentationEvent = entryAttributionSelection
    ? (entryAttributionSelection as string)
    : entry.behavior.behaviors?.[1]?.name;
  const measurement: BehaviorShowClause[`measurement`] = {
    math: entry.measurement.aggregationOperator || RETENTION_MATH_TYPES.UNIQUE,
    perUserAggregation: null,
    rolling: null,
    cumulative: entry.measurement.cumulative || false,
    retentionCumulative: entry.measurement.retentionCumulative || false,
    property: entry.measurement.property
      ? convertPropertySelectiontoBookmarkProperty(entry.measurement.property)
      : null,
    retentionBucketIndex: entry.measurement.retentionBucketIndex,
    retentionSegmentationEvent: metricType === MetricType.Retention ? retentionSegmentationEvent : null,
    segmentMethod: entry.measurement.countUsersOnceType,
    multiAttribution: entry.measurement.multiAttribution,
  };
  return ShowClause.createShowClause({
    isHidden: entry.isHidden ?? false,
    isExpanded: entry.isExpanded ?? false,
    behavior,
    measurement,
  });
}

export function convertPropertySelectiontoBookmarkProperty(property: PropertySectionItem) {
  const {resourceType, value} = property;
  let customProperty,
    customPropertyId,
    dataGroupId,
    tempDataGroupId,
    joinPropertyType,
    propertyDefaultType,
    propertyObjectKey,
    scdEvent,
    type,
    unit,
    listItemGroup;

  if (isProperty(property)) {
    ({
      customProperty,
      customPropertyId,
      dataGroupId,
      tempDataGroupId,
      joinPropertyType,
      propertyDefaultType,
      propertyObjectKey,
      scdEvent,
      type,
      unit,
      listItemGroup,
    } = property);
  }
  return {
    ...(customProperty ? {customProperty} : {}),
    ...(customPropertyId ? {customPropertyId} : {}),
    dataset: DATASET_MIXPANEL,
    ...(dataGroupId ? {dataGroupId} : {}),
    ...(tempDataGroupId ? {tempDataGroupId} : {}),
    ...(joinPropertyType ? {joinPropertyType} : {}),
    ...(listItemGroup ? {listItemGroup} : {}),
    defaultType: propertyDefaultType,
    ...(propertyObjectKey ? {propertyObjectKey} : {}),
    ...(scdEvent ? {scdEvent} : {}),
    name: value,
    resourceType: QUERY_BUILDER_RESOURCE_TYPE_MAPPING[resourceType],
    type,
    unit: unit ?? null,
  };
}

function generateRetentionBuckets({
  showIndex,
  retentionBucketIndex,
  retentionType,
  retentionUnit,
  dateUnit,
  retentionCustomBucketSizes,
  reportSectionsMetadata,
}: {
  showIndex: number;
  retentionBucketIndex: number;
  retentionType: RetentionType;
  retentionUnit: TimeUnit;
  dateUnit: BirthIntervalUnit;
  retentionCustomBucketSizes: number[];
  reportSectionsMetadata: ReportSectionsMetadata;
}): Array<SectionItem<number>> {
  let items: Array<SectionItem<number>> = [];
  const retentionBucketsSize = reportSectionsMetadata
    ? reportSectionsMetadata.show[showIndex].retention_buckets_size
    : 0;
  if (retentionBucketsSize > 0) {
    items = new Array(retentionBucketsSize).fill(null).map((_, i) => {
      return {
        label: getRetentionBucketName({
          bucketIndex: i,
          retentionType,
          retentionUnit,
          dateUnit,
          bucketSizes: retentionCustomBucketSizes,
        }),
        value: i,
        isSelected: i === retentionBucketIndex,
      };
    });
  } else {
    items = [
      {
        label: getRetentionBucketName({
          bucketIndex: retentionBucketIndex,
          retentionType,
          retentionUnit,
          dateUnit,
          bucketSizes: retentionCustomBucketSizes,
        }),
        value: 0,
        isSelected: true,
      },
    ];
  }

  return items;
}
